import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ComponentException } from '@inter-ikea-kompis/exceptions';
import { KeyboardTypeEnum } from '@inter-ikea-kompis/keyboard-manager';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import KeyboardKeyCodeEnum from '../../enums/KeyboardKeyCodeEnum.js';
import KeyboardButtonStyle from './KeyboardButtonStyle.js';

export default class KeyboardButton extends KompisElement {
	// Key code
	@property({ type: String })
	public keyCode: KeyboardKeyCodeEnum | null = null;

	// Type of keyboard.
	@property({ type: String })
	public type: KeyboardTypeEnum = KeyboardTypeEnum.text;

	// Locale is used to determine the type of decimal symbol (comma or point) on the "decimal" keyboard layout.
	@property({ type: String })
	public locale: string | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private listeners = {
		mouseUp: this.onMouseUp.bind(this),
		mouseDown: this.onMouseDown.bind(this),
		mouseLeave: this.onMouseLeave.bind(this)
	};

	private active = false;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${KeyboardButtonStyle.getStyle({
					theme: this.theme,
					keyCode: this.keyCode,
					type: this.type,
					isActive: this.active
				})}
			</style>
			<button
				@mousedown="${this.listeners.mouseDown}"
				@mouseup="${this.listeners.mouseUp}"
				@mouseleave="${this.listeners.mouseLeave}"
				@touchstart="${this.listeners.mouseDown}"
				@touchend="${this.listeners.mouseUp}"
			>
				<div>${this.getKeyText()}</div>
			</button>
		`;
	}

	/**
	 * Return key text.
	 *
	 * @returns Key text.
	 */
	private getKeyText(): string | null {
		switch (this.keyCode) {
			case KeyboardKeyCodeEnum.backspace:
				return '⟵';
			case KeyboardKeyCodeEnum.space:
				return '␣';
			case KeyboardKeyCodeEnum.decimal:
				return this.getDecimalSymbol();
		}

		return this.keyCode;
	}

	/**
	 * Returns the decimal key text.
	 *
	 * @returns Key text.
	 */
	private getDecimalSymbol(): string {
		if (!this.locale) {
			throw new ComponentException(
				'Failed get decimal symbol. The prop "locale" has not been defined.'
			);
		}
		return (1.1).toLocaleString(this.locale).includes(',') ? ',' : '.';
	}

	/**
	 * Handles mouse down event.
	 *
	 * @param event HTMLEvent.
	 */
	private onMouseDown(event: Event): void {
		if (
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(event as any).sourceCapabilities &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(event as any).sourceCapabilities.firesTouchEvents &&
			event.type === 'mousedown'
		) {
			return;
		}

		this.active = true;
		this.update();
	}

	/**
	 * Handles mouse up event.
	 *
	 * @param event HTMLEvent.
	 */
	private onMouseUp(event: Event): void {
		if (
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(event as any).sourceCapabilities &&
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(event as any).sourceCapabilities.firesTouchEvents &&
			event.type === 'mouseup'
		) {
			return;
		}

		this.active = false;
		this.update();
	}

	/**
	 * Handles mouse leave.
	 */
	private onMouseLeave(): void {
		if (this.active) {
			this.active = false;
			this.update();
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-keyboard-button': KeyboardButton;
	}
}
